import { useEffect, useMemo, useState } from 'react';
import {
  FiAnchor,
  FiChevronRight,
  FiClock,
  FiMapPin,
  FiMessageSquare,
  FiSlash,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate } from 'react-router';

import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components';
import { Skeleton } from '@/components/Skeleton';
import { BaseWidget } from '@/components/Widget/BaseWidget';
import { useRoles } from '@/hooks/useRoles';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store';
import { EventParticipantType, isDated } from '@/store/events/events.dto';
import { deleteEventParticipantsApi, useEvents } from '@/store/events/events.store';
import { usePerson } from '@/store/persons/person.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { isOwnerGroupRole, UserRole } from '@/utils/roles';

import { ManageManifestModal } from './ManageManifestModal';
import {
  deriveViewRole,
  mapEventToOption,
  PRESENCE_LABEL,
  PRESENCE_LABEL_SHORT,
  shortEventLine,
  toBackendPresence,
  toPresence,
} from './WhosOnBoardWidget.constants';
import type { Person, Presence, ViewRole } from './WhosOnBoardWidget.constants';
import styles from './WhosOnBoardWidget.module.scss';

const PRESENCE_CLASS: Record<Presence, string> = {
  'on-board': styles.status_onboard,
  ashore: styles.status_ashore,
};

const PRESENCE_ICON: Record<Presence, React.ReactNode> = {
  'on-board': <FiAnchor />,
  ashore: <FiMapPin />,
};

const TILES: { key: 'all' | Presence; icon: React.ReactNode; label: string; tone: string }[] = [
  { key: 'ashore', icon: <FiMapPin />, label: PRESENCE_LABEL.ashore, tone: 'toneWarn' },
  { key: 'on-board', icon: <FiAnchor />, label: PRESENCE_LABEL['on-board'], tone: 'toneTeal' },
  { key: 'all', icon: <FiUsers />, label: 'Total', tone: 'toneNeutral' },
];

const SELECTED_EVENT_KEY = 'whos-on-board:selected-event';
const SKELETON_PEOPLE = 3;
/** The guest's own card draws the largest avatar; the roster rows are 40. */
const AVATAR_BOX = 64;

interface WhosOnBoardWidgetProps {
  role?: ViewRole;
}

export const WhosOnBoardWidget: React.FC<WhosOnBoardWidgetProps> = ({ role: roleOverride }) => {
  const { userRoles } = useRoles();
  const { people, currentUser, getPeople, me, setPresence } = usePerson();
  const { vessel } = useVessels();
  const { eventsList, getAllEvents } = useEvents();
  const inviteUser = useAuth((s) => s.inviteUser);
  const getOrCreateOneToOneRoom = useChats((s) => s.getOrCreateOneToOneRoom);
  const navigate = useNavigate();

  const vesselId = vessel?.id ?? null;
  const role = roleOverride ?? deriveViewRole(userRoles ?? []);
  const canEditAny = role === 'owner' || role === 'captain';

  const [selectedEventId, setSelectedEventId] = useState<string>(
    () => window.localStorage.getItem(SELECTED_EVENT_KEY) ?? '',
  );
  const [presenceFilter, setPresenceFilter] = useState<'all' | Presence>('all');
  const [manageOpen, setManageOpen] = useState(false);
  // One flag for the two requests the layout depends on, so the card swaps from
  // skeleton to content once instead of rebuilding as each lands. Not the stores'
  // own flags: person `loading` also flips on every presence toggle, and a failed
  // events request leaves `eventsList` null forever.
  const [ready, setReady] = useState(false);

  const openChat = async (person: Person) => {
    const room = await getOrCreateOneToOneRoom(person.id);
    navigate(`/inbox/${room.id}`);
  };

  useEffect(() => {
    void Promise.allSettled([getPeople(), getAllEvents()]).finally(() => setReady(true));
    // Only picks the chat button or its same-size placeholder, so it isn't waited on.
    void me();
  }, [getPeople, me, getAllEvents]);

  const events = useMemo(() => {
    const now = new Date().getTime();
    return (eventsList?.items ?? [])
      .filter(isDated)
      .filter((e) => e.time_slot.start && new Date(e.time_slot.end).getTime() >= now)
      .sort((a, b) => new Date(a.time_slot.start).getTime() - new Date(b.time_slot.start).getTime())
      .map(mapEventToOption);
  }, [eventsList]);
  const activeEvent = events.find((e) => e.id === selectedEventId) ?? events[0];

  const selectEvent = (id: string) => {
    setSelectedEventId(id);
    window.localStorage.setItem(SELECTED_EVENT_KEY, id);
  };

  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);

  const selectedEvent = useMemo(
    () => (eventsList?.items ?? []).find((e) => e.id === activeEvent?.id) ?? null,
    [eventsList, activeEvent],
  );

  const vesselRoster: Person[] = useMemo(() => {
    if (!vesselId) return [];
    return people
      .filter((person) => person.services?.some((service) => service.service_id === vesselId))
      .map((person) => {
        const svc = person.services?.find((service) => service.service_id === vesselId);
        return {
          id: person.id,
          firstName: person.person_info.first_name,
          lastName: person.person_info.last_name,
          role: svc?.role ?? '',
          presence: toPresence(svc?.presence),
          avatar: imageVariantLink(person.avatar, AVATAR_BOX) ?? undefined,
        };
      })
      .sort((a, b) => Number(isOwnerGroupRole(b.role)) - Number(isOwnerGroupRole(a.role)));
  }, [people, vesselId]);

  const eventManifest: Person[] = useMemo(
    () =>
      (selectedEvent?.participants ?? [])
        .filter(
          (p) => p.type === EventParticipantType.CREW || p.type === EventParticipantType.GUEST,
        )
        // The event keeps everyone ever added to it, the deleted and those who left the vessel
        // included: only who is on the vessel now is aboard.
        .flatMap((p) => {
          const person = peopleById.get(p.person_id);
          const svc = person?.services?.find((s) => s.service_id === vesselId);
          if (!person || !svc) return [];

          return [
            {
              id: p.person_id,
              firstName: person.person_info.first_name,
              lastName: person.person_info.last_name,
              role: svc.role,
              presence: toPresence(svc.presence),
              avatar: imageVariantLink(person.avatar, AVATAR_BOX) ?? undefined,
            },
          ];
        })
        .sort((a, b) => Number(isOwnerGroupRole(b.role)) - Number(isOwnerGroupRole(a.role))),
    [selectedEvent, peopleById, vesselId],
  );

  const manifest = eventManifest.length > 0 ? eventManifest : vesselRoster;

  const guestSelf: Person | null = useMemo(() => {
    if (!currentUser || !vesselId) return null;
    const svc = currentUser.services?.find((s) => s.service_id === vesselId);
    return {
      id: currentUser.id,
      firstName: currentUser.person_info.first_name,
      lastName: currentUser.person_info.last_name,
      role: svc?.role ?? '',
      presence: toPresence(svc?.presence),
      avatar: imageVariantLink(currentUser.avatar, AVATAR_BOX) ?? undefined,
    };
  }, [currentUser, vesselId]);

  const setPresenceFor = (id: string, next: Presence) =>
    void setPresence(id, toBackendPresence(next));

  const removePerson = async (id: string) => {
    if (!selectedEvent) return;
    await deleteEventParticipantsApi(selectedEvent.id, [id]);
    await getAllEvents();
  };

  const inviteGuest = async ({ name, contact }: { name: string; contact: string }) => {
    if (!vesselId || !selectedEvent) return;
    await inviteUser(contact, name, UserRole.GUEST, vesselId, 'vessel', vessel?.vessel_name, '', {
      id: selectedEvent.id,
    });
  };

  if (role === 'vendor') {
    return (
      <BaseWidget headerTitle="Who’s Aboard" headerIcon={<FiUsers />}>
        <div className={`${styles.content} ${styles.blockedState}`}>
          <FiSlash className={styles.stateIcon} />
          <div className={styles.stateTitle}>Not available for vendors</div>
          <div className={styles.stateDesc}>
            Vendor accounts can’t see who’s aboard, during a trip or between trips.
          </div>
        </div>
      </BaseWidget>
    );
  }

  if (role === 'guest') {
    return <GuestStatusCard self={guestSelf} eventName={activeEvent?.name} />;
  }

  const presenceCounts: Record<Presence, number> = {
    'on-board': manifest.filter((p) => p.presence === 'on-board').length,
    ashore: manifest.filter((p) => p.presence === 'ashore').length,
  };
  const visiblePeople =
    presenceFilter === 'all' ? manifest : manifest.filter((p) => p.presence === presenceFilter);

  return (
    <>
      <BaseWidget
        headerTitle="Who’s Aboard"
        headerIcon={<FiUsers />}
        footer={
          <button type="button" className={styles.manageBtn} onClick={() => setManageOpen(true)}>
            Manage Manifest <FiChevronRight aria-hidden="true" />
          </button>
        }
      >
        <div className={styles.content}>
          {!ready ? (
            <div className={styles.eventSelect} aria-hidden="true">
              <FiClock className={styles.eventSelectIcon} />
              <span className={styles.eventSelectValue}>
                <Skeleton variant="text" width="60%" className={styles.textBone} />
              </span>
            </div>
          ) : (
            activeEvent && (
              <div className={styles.eventSelect}>
                <FiClock className={styles.eventSelectIcon} />
                <span className={styles.eventSelectValue}>{shortEventLine(activeEvent)}</span>
              </div>
            )
          )}

          <div className={styles.tiles}>
            {TILES.map((tile) => {
              const active = presenceFilter === tile.key;
              const count = tile.key === 'all' ? manifest.length : presenceCounts[tile.key];
              return (
                <button
                  type="button"
                  key={tile.key}
                  className={`${styles.tile} ${styles[tile.tone]} ${active ? styles.tileActive : ''}`}
                  aria-pressed={active}
                  onClick={() => setPresenceFilter(active ? 'all' : tile.key)}
                >
                  <span className={styles.tileTop}>
                    <span className={styles.tileIcon} aria-hidden="true">
                      {tile.icon}
                    </span>
                    <span className={styles.tileCount}>
                      {ready ? (
                        count
                      ) : (
                        <Skeleton variant="text" width={24} className={styles.textBone} />
                      )}
                    </span>
                  </span>
                  <span className={styles.tileLabel}>{tile.label}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.peopleList} role="list">
            {!ready ? (
              Array.from({ length: SKELETON_PEOPLE }, (_, i) => (
                <div
                  key={i}
                  className={`${styles.personRow} ${styles.skeletonRow}`}
                  aria-hidden="true"
                >
                  <Skeleton variant="circle" />
                  <span className={styles.personName}>
                    <Skeleton variant="text" width="50%" className={styles.textBone} />
                  </span>
                  <Skeleton variant="block" className={styles.toggleBone} />
                  <span className={styles.chatBtnPlaceholder} />
                </div>
              ))
            ) : visiblePeople.length === 0 ? (
              <div className={styles.manageEmpty}>No one’s aboard yet.</div>
            ) : (
              visiblePeople.map((p) => (
                <div key={p.id} className={styles.personRow}>
                  <Avatar firstName={p.firstName} lastName={p.lastName} src={p.avatar} size={40} />
                  <span className={styles.personName}>
                    {p.firstName} {p.lastName}
                  </span>
                  <div
                    className={styles.presenceToggle}
                    role="group"
                    aria-label={`${p.firstName} boarding status`}
                  >
                    <button
                      type="button"
                      className={`${styles.seg} ${p.presence === 'ashore' ? styles.segOffActive : ''}`}
                      aria-pressed={p.presence === 'ashore'}
                      disabled={!canEditAny}
                      onClick={() => setPresenceFor(p.id, 'ashore')}
                    >
                      {PRESENCE_LABEL_SHORT.ashore}
                    </button>
                    <button
                      type="button"
                      className={`${styles.seg} ${p.presence === 'on-board' ? styles.segOnActive : ''}`}
                      aria-pressed={p.presence === 'on-board'}
                      disabled={!canEditAny}
                      onClick={() => setPresenceFor(p.id, 'on-board')}
                    >
                      {PRESENCE_LABEL_SHORT['on-board']}
                    </button>
                  </div>
                  {p.id !== currentUser?.id ? (
                    <button
                      type="button"
                      className={styles.chatBtn}
                      aria-label={`Message ${p.firstName} ${p.lastName}`}
                      onClick={() => void openChat(p)}
                    >
                      <FiMessageSquare />
                    </button>
                  ) : (
                    <span className={styles.chatBtnPlaceholder} aria-hidden />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </BaseWidget>

      <ManageManifestModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        people={manifest}
        events={events}
        selectedEventId={activeEvent?.id ?? ''}
        currentUserId={currentUser?.id}
        onSelectEvent={selectEvent}
        onRemovePerson={removePerson}
        onInviteGuest={inviteGuest}
      />
    </>
  );
};

interface GuestStatusCardProps {
  self: Person | null;
  eventName?: string;
}

const GuestStatusCard = ({ self, eventName }: GuestStatusCardProps) => (
  <BaseWidget headerTitle="My Status" headerIcon={<FiAnchor />}>
    {self && (
      <div className={`${styles.content} ${styles.guestBody}`}>
        <Avatar firstName={self.firstName} lastName={self.lastName} src={self.avatar} size={64} />
        <div className={styles.guestName}>
          {self.firstName} {self.lastName}
        </div>
        <div className={styles.guestPrompt}>
          Your status{eventName ? ` for ${eventName}` : ''}, as set by the crew.
        </div>
        <span className={`${styles.guestStatus} ${PRESENCE_CLASS[self.presence]}`}>
          {PRESENCE_ICON[self.presence]}
          {PRESENCE_LABEL[self.presence]}
        </span>
      </div>
    )}
  </BaseWidget>
);
